import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  getScheduleStandings,
  type StandingsGroupBy,
} from '@sportninja/common/services/competition/getScheduleStandings'
import { toStandingsRows } from '@sportninja/common/features/competition/operations/utils/inDivisionRecord'
import type { StandingsRow } from '@sportninja/common/features/competition/operations/types/standings.types'

/** Standings change only as games are played — a minute of cache is plenty. */
export const STANDINGS_STALE_TIME = 60_000

interface UseDivisionStandingsArgs {
  /** The division's or conference's own scheduleId. */
  scheduleId: string | null
  /**
   * The level `scheduleId` names. The API scopes its response by this, so it
   * must match the node's own type or the response comes back for the wrong
   * grouping.
   */
  groupBy?: StandingsGroupBy
  /**
   * Gate for the request. The Operations panel otherwise costs zero extra
   * calls per selection, so this stays false until the Standings tab is opened
   * on a division that publishes standings.
   */
  enabled: boolean
}

/**
 * Loads a division's in-division records for the Standings tab.
 *
 * This is the ONLY place the Operations tab fetches standings data. It is
 * deliberately lazy: nothing is requested until `enabled` goes true, which
 * keeps selecting a division free for anyone who never opens the tab.
 */
export const useDivisionStandings = ({
  scheduleId,
  enabled,
  groupBy = 'Division',
}: UseDivisionStandingsArgs) => {
  'use memo'

  const query = useQuery({
    queryKey: queryKeys.operations.standings(scheduleId, groupBy),
    queryFn: () => getScheduleStandings(scheduleId as string, groupBy),
    enabled: enabled && Boolean(scheduleId),
    staleTime: STANDINGS_STALE_TIME,
  })

  const rows: StandingsRow[] = toStandingsRows(query.data)

  return {
    rows,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}
